import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@xefi/x-react/button';
import { Input, Select, type SelectOption } from '@xefi/x-react/form';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { FieldArray } from 'formik';
import { t } from 'i18next';
import { Fragment } from 'react';
import { useUserQuery } from '@/app/(survey-taking)/_hooks/useUserQuery';
import type { Session } from '@/models/event';
import type { PortailUserModel } from '@/models/portailUser';
import type { Participant, Question } from '@/models/question';
import { SurveyTakingQuestion } from './SurveyTakingQuestion';
dayjs.extend(isSameOrAfter);
type ParticipantBlockProps = {
  sessions: Session[];
  question: Partial<Question>;
  path: string;
  handleChange: (path: string, value: unknown) => void;
  indexLabel: string;
  readonly?: boolean;
  errorMap?: Map<string, string>;
};
export const createParticipant = (questions: Question[], indexBlocParticipant: number | undefined, user?: PortailUserModel): Participant => {
  if (!indexBlocParticipant && user) {
    return {
      sessions: [],
      firstName: user.firstname || '',
      lastName: user.lastname || '',
      email: user.email || '',
      questions
    };
  } else {
    return {
      sessions: [],
      firstName: '',
      lastName: '',
      email: '',
      questions
    };
  }
};
export const ParticipantBlock = ({
  sessions,
  question,
  path,
  handleChange,
  indexLabel,
  readonly,
  errorMap
}: ParticipantBlockProps) => {
  const {
    data: user
  } = useUserQuery({
    enabled: true
  });
  return <FieldArray name={`${path}.participant`}>
         {arrayHelper => <div className='flex flex-col gap-6 text-sm'>
               {question.participant?.map((participant, index) => {
        const participantId = `participant-${index}`;
        return <div key={participantId} id={`participants-block-${participantId}`} className='flex flex-col gap-6'>
                        <div className='flex items-center justify-between'>
                           <p>
                              {index === 0 ? `${t('survey.yourInfo')}` : `${t('companion.label', {
                number: index
              })}`}
                           </p>
                           <button type='button' disabled={readonly} aria-label={t('participant.remove')} onClick={() => arrayHelper.remove(index)}>
                              <FontAwesomeIcon icon={faTrash} />
                           </button>
                        </div>
                        <div className='flex flex-col gap-4'>
                           <div className='flex flex-col gap-1'>
                              <p>
                                 {t('session.label')}{' '}
                                 {sessions[0]?.event?.label && `${t('session.linked', {
                  eventName: sessions[0]?.event?.label
                })}`}
                              </p>
                              <Select radius='md' selectionMode='multiple' options={sessions.map<SelectOption>(session => {
                const maxCapacity = (session?.capacity ?? 0) + (session?.overflow ?? 0);
                const isFull = (session?.guests_count ?? 0) >= maxCapacity;
                const remainingPlaces = Math.max(0, maxCapacity - (session?.guests_count ?? 0));
                return {
                  key: String(session.id),
                  label: session.label ?? '',
                  description: `${t('session.places', {
                    count: remainingPlaces
                  })}${isFull ? ` • ${t('session.full')}` : ''}`
                };
              })} value={new Set((participant.sessions ?? []).map(id => String(id)))} onSelectionChange={selection => {
                let selectedArray: string[] = [];
                if (selection == null) {
                  selectedArray = [];
                } else if (selection instanceof Set) {
                  selectedArray = Array.from(selection as Set<string>);
                } else if (Array.isArray(selection)) {
                  selectedArray = selection as string[];
                } else {
                  selectedArray = [String(selection)];
                }
                const allowedSelected = selectedArray.filter(key => {
                  const session_0 = sessions.find(({
                    id: id_0
                  }) => String(id_0) === key);
                  if (!session_0) return false;
                  const maxCapacity_0 = (session_0.capacity ?? 0) + (session_0.overflow ?? 0);
                  const isFull_0 = (session_0.guests_count ?? 0) >= maxCapacity_0;
                  return !isFull_0;
                });
                const converted = allowedSelected.map(key_0 => {
                  const original = sessions.find(({
                    id: id_1
                  }) => String(id_1) === key_0)?.id;
                  return original;
                }).filter(item => item !== undefined) as Array<(typeof sessions)[number]['id']>;
                handleChange(`participant[${index}].sessions`, converted);
              }} />
                           </div>
                           <Input type='text' radius='md' placeholder={t('participant.lastName') ?? ''} value={participant?.lastName} onChange={newValue => handleChange(`participant[${index}].lastName`, newValue.target.value)} />
                           <Input type='text' radius='md' placeholder={t('participant.firstName') ?? ''} value={participant?.firstName} onChange={newValue_0 => handleChange(`participant[${index}].firstName`, newValue_0.target.value)} />
                           <Input radius='md' type='email' placeholder={t('participant.email') ?? ''} value={participant?.email} onChange={newValue_1 => handleChange(`participant[${index}].email`, newValue_1.target.value)} />
                        </div>
                     </div>;
      })}
               {question.participant?.map((participant_0, index_0) => <Fragment key={`questions-${index_0}`}>
                     {participant_0?.questions && participant_0?.questions?.length > 0 && <div className='flex flex-col gap-6'>
                              {participant_0.questions.map((question_0, i) => <div key={question_0.id}>
                                    <SurveyTakingQuestion indexLabel={`${indexLabel}.${i + 1}`} question={question_0} handleChange={(path_0, value) => handleChange(`participant[${index_0}].questions[${i}].${path_0}`, value)} questionPath={`${path}.participant[${index_0}].questions[${i}]`} sessions={sessions} errorMap={errorMap} />
                                 </div>)}
                           </div>}
                  </Fragment>)}
               {(question?.participant?.length ?? 0) < (sessions[0]?.event?.accompanying_person_limit ?? 1) && !readonly && <Button type='button' onClick={() => {
        const newParticipant = createParticipant(sessions?.at(0)?.event?.custom_fields ?? [], question?.participant?.length, user);
        arrayHelper.push(newParticipant);
      }} className='w-fit'>
                        {t('participant.add')}
                     </Button>}
            </div>}
      </FieldArray>;
};